import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import useSelectionSafeDoubleClick from '../../../hooks/common/useSelectionSafeDoubleClick';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import DsSelect from '../../common/DsSelect';
import { getEventField, updateEventField, type EventFieldSettings } from '../../../api/eventDetails';
import '../../../css/admin/events/EventFieldEditor.css';
const Modal = DsModal as React.ComponentType<any>;
const Select = DsSelect as React.ComponentType<any>;
const EditorContext = createContext<((field: string) => void) | null>(null);
export function EventEditableRow({
  field,
  children,
  enabled = true
}: {
  field: string;
  children: React.ReactNode;
  enabled?: boolean;
}) {
  const open = useContext(EditorContext);
  const show = () => {
    if (enabled) open?.(field);
  };
  const gesture = useSelectionSafeDoubleClick(enabled && !!open, show);
  return <tr className={enabled && open ? 'event-field-row' : undefined} {...gesture}>
    {React.Children.map(children, child => React.isValidElement(child) && child.type === 'th'
      ? React.cloneElement(child as React.ReactElement<any>, { scope: 'row' }) : child)}
  </tr>;

}
export function EventFieldEditorProvider({
  eventId,
  onSaved,
  children,
  actions = {}
}: {
  eventId: string;
  onSaved: () => Promise<unknown>;
  children: React.ReactNode;
  actions?: Record<string, () => void>;
}) {
  const [field, setField] = useState<string>();
  useEffect(() => setField(undefined), [eventId]);
  return <EditorContext.Provider value={key => actions[key] ? actions[key]() : setField(key)}>{children}{field && <EventFieldEditor key={`${eventId}:${field}`} eventId={eventId} field={field} onClose={() => setField(undefined)} onSaved={onSaved} />}</EditorContext.Provider>;
}
export default function EventFieldEditor({
  eventId,
  field,
  onClose,
  onSaved
}: {
  eventId: string;
  field: string;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const active = useRef(true);
  const searchRequest = useRef(0);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const [data, setData] = useState<EventFieldSettings>();
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState('');
  useEffect(() => {
    const abort = new AbortController();
    setLoading(true);
    setError('');
    getEventField(eventId, field, undefined, abort.signal).then(d => {
      if (!abort.signal.aborted) {
        setData(d);
        setRows(d.rows);
      }
    }).catch(e => {
      if (!abort.signal.aborted) setError(e.message);
    }).finally(() => {
      if (!abort.signal.aborted) setLoading(false);
    });
    return () => abort.abort();
  }, [eventId, field, reload]);
  const patch = (index: number, key: string, value: string) => setRows(old => old.map((r, i) => i === index ? {
    ...r,
    [key]: value
  } : r));
  const search = async () => {
    const request = ++searchRequest.current;
    try {
      const found = await getEventField(eventId, field, query);
      if (active.current && request === searchRequest.current) setData(old => old ? {
        ...old,
        controls: found.controls.map(c => ({
          ...c,
          options: [...(c.options || []), ...(old.controls.find(v => v.key === c.key)?.options || []).filter(o => rows.some(r => r[c.key] === o.value) && !c.options?.some(v => v.value === o.value))]
        }))
      } : old);
    } catch (e) {
      if (active.current && request === searchRequest.current) setError((e as Error).message);
    }
  };
  const save = async () => {
    if (!data?.canEdit || saving || loading) return;
    setSaving(true);
    setError('');
    try {
      await updateEventField(eventId, field, data.version, rows);
      if (!active.current) return;
      await onSaved();
      if (active.current) onClose();
    } catch (e) {
      if (active.current) setError((e as Error).message);
    } finally {
      if (active.current) setSaving(false);
    }
  };
  return <Modal open title={`${data?.label || '項目'}を編集`} size="md" onClose={() => {
    if (!saving) onClose();
  }} footer={<DsButton variant="primary" disabled={saving || loading || !data?.canEdit || JSON.stringify(rows) === JSON.stringify(data?.rows)} onClick={save}>{saving ? '保存中…' : '保存'}</DsButton>}>
  {loading && <p role="status">読み込み中…</p>}
  {error && <div role="alert">{error}<DsButton variant="secondary" disabled={saving} onClick={() => setReload(v => v + 1)}>最新の内容を読み込む</DsButton></div>}
  {data?.note && <p>{data.note}</p>}
  {data?.searchable && <div className="event-field-editor__search"><input className="input" aria-label="会場を検索" value={query} maxLength={100} onChange={e => setQuery(e.target.value)} /><DsButton variant="secondary" onClick={search} disabled={loading || saving}>検索</DsButton></div>}
  <fieldset className="event-field-editor" disabled={loading || saving || !data?.canEdit}>
   {rows.map((row, index) => <div className="event-field-editor__item" key={index}>{data?.controls.map(c => <label className="event-field-editor__control" key={c.key}><span>{c.label}</span>
    {c.type === 'select' ? <Select value={row[c.key] || ''} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => patch(index, c.key, e.target.value)}><option value="">未選択</option>{row[c.key] && !c.options?.some(o => o.value === row[c.key]) && <option value={row[c.key]}>現在の選択</option>}{c.options?.map(o => <option value={o.value} key={o.value}>{o.label}</option>)}</Select> : c.type === 'textarea' ? <textarea className="textarea" value={row[c.key] || ''} maxLength={c.maxLength} rows={5} onChange={e => patch(index, c.key, e.target.value)} /> : <input className="input" type={c.type} value={row[c.key] || ''} maxLength={c.maxLength} required={c.required} disabled={c.key === 'cast_name' && !!row.artist_id} onChange={e => patch(index, c.key, e.target.value)} />}
   </label>)}{row.artist_id && <DsButton variant="secondary" onClick={() => patch(index, 'artist_id', '')}>手入力に変更</DsButton>}{data?.list && <DsButton variant="danger" size="sm" onClick={() => setRows(old => old.filter((_, i) => i !== index))}>この行を削除</DsButton>}</div>)}
   {data?.list && <DsButton variant="create" disabled={rows.length >= 100} onClick={() => setRows(old => [...old, Object.fromEntries(data.controls.map(c => [c.key, '']))])}>追加</DsButton>}
  </fieldset>
  {data?.canEdit && <p className="form-hint">この項目だけを保存します。閉じると未保存の入力は破棄されます。</p>}
 </Modal>;
}
